"use client";

import { useActionState } from "react";
import { useFormStatus } from "react-dom";
import { AccountRailAvatar } from "@/components/account-rail-avatar";
import { SaveButtonSpinner } from "@/components/save-button-spinner";
import { loginFromDocs } from "@/app/actions";
import Image from "next/image";
import { LogIn, User } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

export function DocsAccountPrompt({
  kind,
  pending = false,
}: {
  kind: "organization" | "user";
  pending?: boolean;
}) {
  const [loginError, loginAction] = useActionState(loginFromDocs, "");
  if (pending) {
    return (
      <SidebarMenu className="w-auto items-center" aria-label="Loading account">
        <SidebarMenuItem>
          <div
            className={
              kind === "organization"
                ? "flex size-8 items-center justify-center rounded-lg bg-background"
                : "flex size-8 items-center justify-center p-1"
            }
          >
            {kind === "organization" ? (
              <AccountRailAvatar kind="organization" pending fallback={null} />
            ) : (
              <User className="size-4 text-foreground" aria-hidden />
            )}
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }
  return (
    <Dialog>
      <SidebarMenu className="w-auto items-center">
        <SidebarMenuItem>
          <Tooltip>
            <TooltipTrigger asChild>
              <DialogTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  aria-label={
                    kind === "organization"
                      ? "Sign in to Libum Portal"
                      : "Account menu"
                  }
                  className="group/sign-in flex max-h-8 !w-8 shrink-0 items-center !justify-center !gap-0 disabled:opacity-100 md:p-0"
                >
                  {kind === "organization" ? (
                    <Avatar className="size-8 rounded-lg bg-background">
                      <AvatarFallback className="rounded-lg">
                        <LogIn
                          className="size-4 text-foreground transition-colors duration-150 group-hover/sign-in:text-brand-primary group-focus-visible/sign-in:text-brand-primary dark:group-hover/sign-in:text-brand-accent dark:group-focus-visible/sign-in:text-brand-accent"
                          aria-hidden
                        />
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <span className="flex size-8 items-center justify-center rounded-lg bg-sidebar-accent text-foreground">
                      <User className="size-4" aria-hidden />
                    </span>
                  )}
                </SidebarMenuButton>
              </DialogTrigger>
            </TooltipTrigger>
            <TooltipContent side="right" align="center">
              Sign in to Libum Portal
            </TooltipContent>
          </Tooltip>
        </SidebarMenuItem>
      </SidebarMenu>
      <DialogContent
        className="max-h-[90svh] select-none gap-0 overflow-y-auto bg-card p-0 text-gray-1100 dark:text-gray-100 sm:max-w-[420px]"
        showCloseButton={false}
      >
        <div className="relative h-28 overflow-hidden rounded-t-lg" aria-hidden>
          <Image
            src="/images/whats-new-september-04.webp"
            alt=""
            fill
            sizes="420px"
            className="saturate-75 object-cover opacity-60"
            unoptimized
            priority
          />
          <div className="docs-account-landscape-fade absolute inset-0" />
        </div>
        <div className="relative -mt-6 px-6 pb-5 sm:px-8">
          <DialogHeader className="mb-6 items-center space-y-2 text-center">
            <DialogTitle className="sr-only">Libum Portal</DialogTitle>
            <div className="pb-2" aria-hidden="true">
              <Image
                src="/logos/libum_light.svg"
                width={150}
                height={40}
                alt=""
                className="dark:hidden"
                unoptimized
              />
              <Image
                src="/logos/libum_dark.svg"
                width={150}
                height={40}
                alt=""
                className="hidden dark:block"
                unoptimized
              />
            </div>
            <DialogDescription className="flex items-center justify-center gap-1 text-sm text-secondary">
              <span>Sign in or create an account to get started.</span>
              <Image
                src="/twemoji/svg/2728.svg"
                width={16}
                height={16}
                alt=""
                className="shrink-0"
                unoptimized
              />
            </DialogDescription>
          </DialogHeader>
          <form action={loginAction}>
            <LoginButton />
            {loginError && (
              <p role="alert" className="mt-2 text-sm text-destructive">
                {loginError}
              </p>
            )}
          </form>
          <div className="mt-3 text-center">
            <DialogClose asChild>
              <button
                type="button"
                className="rounded-sm p-0 text-sm text-muted underline-offset-4 hover:underline"
              >
                Keep reading
              </button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LoginButton() {
  const { pending } = useFormStatus();
  return (
    <Button
      type="submit"
      disabled={pending}
      aria-busy={pending}
      className="docs-sign-in group h-10 w-full"
    >
      Continue to Libum Portal
      <SaveButtonSpinner
        show={pending}
        collapseGap
        iconClassName="text-current"
      />
      <span role="status" className="sr-only">
        {pending ? "Opening secure sign in…" : ""}
      </span>
    </Button>
  );
}
